import {
  SlidersHorizontal,
  Truck,
  ShieldCheck,
  Puzzle,
  Scale,
  Headphones,
  type LucideIcon,
} from "lucide-react";

export interface FAQItem {
  id: string;
  q: string;
  a: string;
  tags?: string[];
  link?: {
    label: string;
    to: string;
  };
  externalLink?: {
    label: string;
    href: string;
  };
}

export interface FAQCategory {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  icon: LucideIcon;
  badge: string;
  items: FAQItem[];
}

export const FAQ_CATEGORIES: FAQCategory[] = [
  {
    id: "control",
    title: "1. Control, Flexibilidad y Publicación del Set",
    shortTitle: "Control y Calendario",
    description:
      "Tus sets, tus reglas: gestiona la disponibilidad en tu calendario, acepta o declina libremente y define los requisitos de tus sets.",
    icon: SlidersHorizontal,
    badge: "Autonomía total",
    items: [
      {
        id: "control-1",
        q: "¿Tengo la obligación de aceptar cualquier solicitud de préstamo que reciba?",
        a: "En absoluto. Como propietario tienes el control total y exclusivo de tu catálogo. Cuando un prestatario solicita tu set, puedes revisar previamente su perfil, nivel de verificación y valoraciones de la comunidad antes de decidir si aceptas o rechazas la petición con total libertad.",
        tags: ["solicitudes", "control", "perfil", "prestatario"],
      },
      {
        id: "control-2",
        q: "¿Puedo bloquear fechas si quiero montar o exhibir mis sets en casa?",
        a: "Sí, en cualquier momento. Desde tu panel de control puedes bloquear días, semanas o meses completos en el calendario de cada set. Tu set solo estará disponible en los rangos temporales que tú decidas activar.",
        tags: ["calendario", "fechas", "disponibilidad", "bloqueo"],
      },
      {
        id: "control-3",
        q: "¿Qué requisitos debe cumplir un set LEGO® para poder publicarlo?",
        a: "Debe ser un set oficial LEGO® apto para ser montado. No es obligatorio conservar la caja exterior original, siempre y cuando se incluyan todas las piezas de construcción requeridas por las instrucciones y se declare el estado con honestidad (si incluye manuales originales en papel o digitales, o estado de las minifiguras).",
        tags: ["requisitos", "caja", "instrucciones", "manuales"],
      },
      {
        id: "control-4",
        q: "¿Puedo retirar o pausar mis sets cuando lo necesite?",
        a: "Por supuesto. Si deseas pausar temporalmente una publicación o retirar un set de la plataforma de forma definitiva, puedes hacerlo con un solo clic siempre que no haya un préstamo en curso ya confirmado para ese periodo.",
        tags: ["pausar", "retirar", "gestión"],
      },
    ],
  },
  {
    id: "operativa",
    title: "2. Proceso Operativo Paso a Paso (De la solicitud a la devolución)",
    shortTitle: "Operativa y Envíos",
    description:
      "Flujo ágil y sin complicaciones: cómo gestionar solicitudes, preparar el set, entregarlo en mano o mediante taquilla y recibirlo de vuelta.",
    icon: Truck,
    badge: "Logística ágil",
    items: [
      {
        id: "operativa-1",
        q: "¿Cómo se realiza la entrega y devolución de los sets?",
        a: "Dispones de dos modalidades muy sencillas: entrega en mano acordada localmente en tu zona o barrio (0 € de coste de envío, directa entre vecinos) o envío logístico automatizado con entrega en puntos de conveniencia o taquillas inteligentes (tipo InPost) a tu elección para mayor comodidad de horarios sin tener que esperar al repartidor en casa.",
        tags: ["entrega", "devolución", "en mano", "inpost", "taquillas"],
      },
      {
        id: "operativa-hand-delivery",
        q: "¿Cómo hacemos el intercambio \"en mano\"?",
        a: "Brickshare mapea tu ubicación en una zona randomizada alrededor de tu ubicación real por privacidad, mientras que permite tener una idea de tu ubicación física para facilitar que, vía chat, defináis el mejor momento y punto de contacto para la entrega y devolución del set en la modalidad de \"entrega en mano\".",
        tags: ["entrega en mano", "ubicación", "privacidad", "chat", "intercambio"],
      },
      {
        id: "operativa-2",
        q: "¿Cómo debo preparar y empaquetar el set antes de entregarlo?",
        a: "Recomendamos entregar las piezas organizadas en bolsas herméticas (tipo zip) limpias y dentro de una caja o contenedor rígido bien protegido. Si optas por envío logístico, la plataforma genera la etiqueta de transporte lista para pegar y depositar en tu punto más cercano.",
        tags: ["empaquetado", "bolsas zip", "preparación", "etiqueta"],
      },
      {
        id: "operativa-3",
        q: "¿Cuáles son los límites de duración de un préstamo?",
        a: "Los préstamos estándar se configuran en periodos de 2 a 4 semanas para garantizar tiempo suficiente de construcción y disfrute familiar.",
        tags: ["duración", "tiempo", "plazos"],
      },
      {
        id: "operativa-4",
        q: "¿Qué ocurre si el prestatario cancela antes de la entrega?",
        a: "Un usuario puede retornar el set antes de la fecha, pero en ningún caso se considera una cancelación con derecho a retorno del tiempo no disfrutado, ni da derecho a reembolso.",
        tags: ["cancelación", "calendario"],
      },
    ],
  },
  {
    id: "proteccion",
    title: "3. Mecanismos de Protección, Garantías y Verificación",
    shortTitle: "Protección y Garantías",
    description:
      "Seguridad preventiva de primer nivel: verificación oficial universal de identidad y fianzas opcionales configuradas por el propietario.",
    icon: ShieldCheck,
    badge: "Máxima seguridad",
    items: [
      {
        id: "proteccion-1",
        q: "¿Cómo se comprueba la identidad de los prestatarios?",
        a: "La confianza es la base del alquiler. Para poder garantizarla validamos tu teléfono, tu identidad (DNI o pasaporte) y te pedimos un selfie para asegurarnos de que eres tú, sin ocultar nada. Brickshare no tiene acceso directo a estos datos: son custodiados por Stripe conforme a la normativa europea de protección de datos.",
        tags: ["verificación", "identidad", "dni", "biometría"],
        link: {
          label: "Política de privacidad y cookies",
          to: "/privacidad",
        },
      },
      {
        id: "proteccion-2",
        q: "¿Cómo funciona la fianza preventiva de garantía?",
        a: "La fianza es 100% opcional y tú como propietario decides si solicitarla y qué importe fijar según el valor o singularidad de tu set. Si se activa, se realiza una retención bancaria temporal en la tarjeta del prestatario (no es un cobro directo) que permanece asegurada durante el préstamo.",
        tags: ["fianza", "garantía", "depósito", "retención"],
      },
      {
        id: "proteccion-3",
        q: "¿Cuándo se libera la fianza al prestatario?",
        a: "La retención de la fianza se anula y libera de manera automática en un plazo máximo de 48 horas tras recibir el set de vuelta y comprobar que todo está correcto.",
        tags: ["liberación", "cierre", "devolución", "48h"],
      },
      {
        id: "proteccion-4",
        q: "¿Qué respaldo tengo si un prestatario incumple o no devuelve el set?",
        a: "En el caso excepcional de no retorno injustificado, la garantía bancaria retenida se ejecuta para indemnizar al propietario, y el equipo de soporte gestiona la mediación y reclamación correspondiente.",
        tags: ["incumplimiento", "indemnización", "cobertura"],
      },
    ],
  },
  {
    id: "piezas",
    title: "4. Estado del Set, Minifiguras y Piezas Extraviadas",
    shortTitle: "Minifiguras y Piezas",
    description:
      "Protección del valor coleccionista: registro fotográfico de check-in/out, minifiguras y protocolo ágil de reposición de piezas originales.",
    icon: Puzzle,
    badge: "Cuidado del detalle",
    items: [
      {
        id: "piezas-1",
        q: "¿Cómo se deja constancia del estado del set antes y después del préstamo?",
        a: "La operativa incluye un paso de registro fotográfico rápido (check-in y check-out). Tanto el propietario como el prestatario toman fotos de las piezas principales, minifiguras y estado general antes de la entrega y al recibir el paquete, generando un registro visual objetivo.",
        tags: ["fotos", "check-in", "check-out", "registro"],
      },
      {
        id: "piezas-2",
        q: "¿Qué ocurre si al devolver el set falta una pieza o se ha roto?",
        a: "Contamos con un protocolo ágil de reposición: se identifican las piezas originales faltantes (mediante su referencia oficial de LEGO®) y se gestiona su reposición sin coste para el propietario, cubierto por el prestatario o descontado proporcionalmente de su fianza de garantía si estaba configurada.",
        tags: ["piezas rotas", "piezas faltantes", "reposición"],
      },
      {
        id: "piezas-3",
        q: "¿Están protegidas las minifiguras coleccionables?",
        a: "Sí. Las minifiguras son elementos especialmente vigilados en el protocolo de check-in. Puedes fotografiar las figuras individualmente al embalarlas. Si una minifigura no es devuelta o se devuelve alterada, se compensa al propietario para reponer el elemento original exacto.",
        tags: ["minifiguras", "colección", "valor"],
      },
      {
        id: "piezas-4",
        q: "¿Se considera desperfecto el ensamblaje normal de las piezas?",
        a: "No. El montaje y desmontaje normal propio de la afición LEGO® forma parte del uso ordinario. El protocolo de protección se activa ante extravío de piezas, sustitución indebida, rotura física por torsión o devolución incompleta.",
        tags: ["desgaste", "uso normal", "montaje"],
      },
      {
        id: "piezas-5",
        q: "¿Qué pasa si me faltan algunas piezas?",
        a: "Los sets deben alquilarse con el 100% de las piezas. Si te falta alguna, dínoslo y vemos si podemos enviártelas. Si no, te aconsejamos que contactes con nuestros amigos de Brick Clinic, que se dedican, entre otras cosas, a completar sets.",
        externalLink: {
          label: "Visitar Brick Clinic",
          href: "https://www.brickclinic.eu",
        },
        tags: ["piezas faltantes", "completar sets", "brick clinic"],
      },
    ],
  },
  {
    id: "disputas",
    title: "5. Ventana de Revisión (48h), Devolución y Disputas",
    shortTitle: "Revisión 48h y Disputas",
    description:
      "Tranquilidad tras la devolución: ventana de inspección de 48 horas, mediación neutral del equipo Brickshare y resolución objetiva.",
    icon: Scale,
    badge: "Resolución justa",
    items: [
      {
        id: "disputas-1",
        q: "¿De cuánto tiempo dispongo para revisar el set cuando me lo devuelven?",
        a: "Dispones de una ventana de tranquilidad de 48 horas desde que recibes el set de vuelta para abrirlo, comprobar las bolsas y verificar las piezas o minifiguras clave antes de dar el visto bueno final.",
        tags: ["48 horas", "revisión", "inspección", "plazo"],
      },
      {
        id: "disputas-2",
        q: "¿Cómo abro una incidencia si detecto un problema?",
        a: "Directamente desde la pantalla de detalle del préstamo en tu panel de control, haciendo clic en 'Reportar incidencia'. Puedes adjuntar fotografías del estado recibido y describir brevemente lo sucedido en menos de dos minutos.",
        tags: ["incidencia", "reportar", "panel"],
      },
      {
        id: "disputas-3",
        q: "¿Cómo actúa el equipo de mediación de Brickshare en una disputa?",
        a: "Nuestro equipo de soporte actúa como mediador neutral e independiente. Comparamos las fotografías de check-in inicial y check-out de devolución, contrastamos los inventarios y aplicamos una resolución equitativa conforme a las normas de la comunidad.",
        tags: ["mediación", "soporte", "resolución", "imparcialidad"],
      },
      {
        id: "disputas-4",
        q: "¿Qué sucede si el prestatario se retrasa en devolver el set?",
        a: "El sistema envía recordatorios automáticos al prestatario antes y durante la fecha límite. Si existe un retraso injustificado, se aplican recargos de demora por cada día extra de uso para compensar al propietario.",
        tags: ["retraso", "demora", "plazo"],
      },
    ],
  },
  {
    id: "transparencia",
    title: "6. Transparencia Económica y Canales de Soporte",
    shortTitle: "Costes Claros y Soporte",
    description:
      "Sin costes ocultos ni sorpresas desde el minuto cero: visibilidad total de importes y canales de contacto directo para cualquier consulta.",
    icon: Headphones,
    badge: "Cero sorpresas",
    items: [
      {
        id: "transparencia-1",
        q: "¿Hay que pagar alguna cuota mensual o coste oculto por tener mis sets dados de alta?",
        a: "No. En Brickshare no existen cuotas de suscripción periódica ni costes fijos por publicar tus sets. Mantener tu catálogo publicado es completamente gratuito.",
        tags: ["gratis", "sin cuotas", "sin suscripción"],
      },
      {
        id: "transparencia-2",
        q: "¿Cuándo y cómo sé exactamente los importes y compensaciones de cada préstamo?",
        a: "Desde el minuto cero. Antes de aceptar cualquier petición, la pantalla te muestra con total transparencia el desglose completo: duración, importe de fianza si decidiste fijarla y la compensación exacta que recibirás, sin descuentos imprevistos posteriores.",
        tags: ["minuto cero", "desglose", "transparencia", "compensación"],
      },
      {
        id: "transparencia-3",
        q: "¿Por qué canales puedo contactar con soporte si tengo alguna duda?",
        a: "Tienes a tu disposición el canal de asistencia directa desde tu panel de usuario, soporte prioritario por correo electrónico (soporte@brickshare.org o info@brickshare.org) y nuestra sección de contacto directo. Respondemos ágilmente para ayudarte en cualquier etapa de la operativa.",
        tags: ["contacto", "soporte", "email", "chat"],
      },
      {
        id: "transparencia-4",
        q: "¿Cómo ayuda este modelo a revalorizar y cuidar mi colección?",
        a: "En lugar de tener sets cogiendo polvo en cajas o estanterías, permites que otros fans responsables los disfruten, mantienes vivo el interés de tu colección y obtienes un rendimiento constante bajo un entorno seguro y asegurado.",
        tags: ["revalorizar", "colección", "comunidad", "sostenible"],
      },
    ],
  },
];

/**
 * Devuelve todas las preguntas y respuestas aplanadas (útil para schema SEO y búsqueda global)
 */
export function getAllFaqItems(): FAQItem[] {
  return FAQ_CATEGORIES.flatMap((category) => category.items);
}
